import { useState, useRef, useEffect, useCallback } from 'react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { FontFamily } from '@tiptap/extension-font-family';
import Typography from '@tiptap/extension-typography';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { TableKit } from '@tiptap/extension-table';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import { useLanguage } from '../../../i18n/LanguageContext';
import { FontSize, Proofreading, Spellcheck, ResizableImage, PageBreakSpacer, Indent, UserColor, TextOutline, Screenplay, ParagraphSpacing, Pagination, WriterNote } from '../extensions';
import type { ProofreadSettings } from '../modals';
import { toSpellLanguage } from '../spellcheck/language';
import {
    PAGE_BREAK_HEIGHT_PX,
    PAGE_CONTENT_HEIGHT_PX,
    PAGE_EDGE_OVERHANG_PX,
    PAGE_MARGIN_PX,
    PAGE_SHEET_CHROME_PX,
    PAGE_SHEET_GAP_PX,
} from '../utils/pageGeometry';

interface UseDocumentEditorProps {
    proofreadSettings: ProofreadSettings;
    customDictionary: string[];
    onAddToDictionary: (word: string) => void;
    printLayout: boolean;
    onUpdate: (content: string) => void;
}

export const useDocumentEditor = ({
    proofreadSettings,
    customDictionary,
    onAddToDictionary,
    printLayout,
    onUpdate,
}: UseDocumentEditorProps) => {
    // Hook inside a component: the placeholder can come from the language pack
    const { t, language } = useLanguage();
    const spellcheckLabels = {
        addToDictionary: t.editor.spellAddToDictionary,
        ignore: t.editor.spellIgnore,
        noSuggestions: t.editor.spellNoSuggestions,
    };
    const [editorKey, setEditorKey] = useState(0);
    // Reported by the pagination plugin, which is the only thing that knows
    // where the page breaks actually landed
    const [pageCount, setPageCount] = useState(1);
    const editorReadyRef = useRef<boolean>(false);
    const pendingContentRef = useRef<string>('');
    const lastContentRef = useRef<string>('');
    const onPageCountChange = useCallback((count: number) => setPageCount(count), []);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3, 4, 5, 6],
                },
                link: {
                    openOnClick: false,
                    autolink: true,
                    defaultProtocol: 'https',
                },
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            TextStyle,
            FontSize,
            Color,
            UserColor,
            TextOutline,
            Highlight.configure({
                multicolor: true,
            }),
            FontFamily,
            ResizableImage.configure({
                inline: false,
                allowBase64: false,
            }),
            TableKit.configure({
                table: { resizable: true },
            }),
            Superscript,
            Subscript,
            Indent,
            ParagraphSpacing,
            Screenplay,
            WriterNote,
            Typography,
            Placeholder.configure({
                placeholder: t.editor.placeholder,
            }),
            TaskList,
            TaskItem.configure({
                nested: true,
            }),
            Proofreading.configure({
                enabled: false,
                settings: proofreadSettings,
                customDictionary: customDictionary,
            }),
            Spellcheck.configure({
                enabled: proofreadSettings.spelling,
                fallbackLanguage: toSpellLanguage(language),
                customDictionary,
                onAddToDictionary,
                labels: spellcheckLabels,
            }),
            PageBreakSpacer.configure({
                autoPagination: false,
                pageHeight: 1255,
            }),
            Pagination.configure({
                pageHeight: PAGE_CONTENT_HEIGHT_PX,
                breakHeight: PAGE_BREAK_HEIGHT_PX,
                sheetChrome: PAGE_SHEET_CHROME_PX,
                sheetGap: PAGE_SHEET_GAP_PX,
                pageMargin: PAGE_MARGIN_PX,
                edgeOverhang: PAGE_EDGE_OVERHANG_PX,
                mode: printLayout ? 'sheets' : 'rule',
                onPageCountChange,
            }),
        ],
        content: '',
        onUpdate: ({ editor: updatedEditor }) => {
            const html = updatedEditor.getHTML();
            if (html !== lastContentRef.current) {
                lastContentRef.current = html;
                onUpdate(html);
            } else {
            }
        },
        onCreate: ({ editor: createdEditor }) => {
            editorReadyRef.current = true;
            
            // If we have pending content, set it now
            if (pendingContentRef.current) {
                createdEditor.commands.setContent(pendingContentRef.current);
                lastContentRef.current = pendingContentRef.current;
                pendingContentRef.current = '';
            }
        },
        editorProps: {
            attributes: {
                // spellcheck and lang come from the Spellcheck extension
                class: 'document-editor ProseMirror',
            },
            handleClick: (view, pos, event) => {
                const target = event.target as HTMLElement;
                // If clicking on the editor background (not on content), clear node selection
                if (target.classList.contains('ProseMirror') || 
                    target.classList.contains('document-editor')) {
                    const { state, dispatch } = view;
                    const { selection } = state;
                    // If a node is selected, clear it
                    if (selection instanceof NodeSelection) {
                        const tr = state.tr.setSelection(TextSelection.create(state.doc, pos));
                        dispatch(tr);
                        return true;
                    }
                }
                return false;
            },
        },
    }, [editorKey]);

    // Update proofreading settings when they change. The editor instance can
    // exist while not (or no longer) fully initialized — e.g. on a hard
    // refresh inside a document or right after an editorKey remount — so
    // guard against a missing extensionManager/view instead of crashing.
    useEffect(() => {
        if (editor && !editor.isDestroyed && editor.extensionManager && editor.view) {
            const styleChecksOn =
                proofreadSettings.grammar ||
                proofreadSettings.activeVoice ||
                proofreadSettings.conciseness ||
                proofreadSettings.sentenceSplit;
            editor.extensionManager.extensions.forEach((ext) => {
                if (ext.name === 'proofreading') {
                    (ext.options as any).enabled = styleChecksOn;
                    (ext.options as any).settings = proofreadSettings;
                    (ext.options as any).customDictionary = customDictionary;
                }
                if (ext.name === 'spellcheck') {
                    Object.assign(ext.options, {
                        enabled: proofreadSettings.spelling,
                        fallbackLanguage: toSpellLanguage(language),
                        customDictionary,
                        onAddToDictionary,
                        labels: spellcheckLabels,
                    });
                }
            });
            // Force update of the editor to apply new settings
            editor.view.dispatch(editor.state.tr);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proofreadSettings, customDictionary, onAddToDictionary, language, t, editor]);

    // Print layout flips between paper sheets and a plain break rule. The
    // extension's options are read on every measuring pass, so updating them
    // in place and nudging the editor is enough to re-draw.
    useEffect(() => {
        if (!editor || editor.isDestroyed || !editor.extensionManager || !editor.view) return;
        const pagination = editor.extensionManager.extensions.find((ext) => ext.name === 'pagination');
        if (!pagination) return;
        (pagination.options as any).mode = printLayout ? 'sheets' : 'rule';
        editor.view.dispatch(editor.state.tr);
    }, [printLayout, editor]);

    return {
        editor,
        editorKey,
        setEditorKey,
        pageCount,
        editorReadyRef,
        pendingContentRef,
        lastContentRef,
    };
};
